import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Loader2, Crown, Sparkles, Building2 } from "lucide-react";

const PricingPage = () => {
  const { api, user, subscription } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get("/subscription/plans");
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const response = await api.post("/subscription/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Failed to create checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setSubscribing(null);
    }
  };

  const planIcons = {
    starter: <Sparkles className="w-6 h-6" />,
    pro: <Crown className="w-6 h-6" />,
    enterprise: <Building2 className="w-6 h-6" />,
  };

  const currentPlan = subscription?.plan_id;
  const isActive = subscription?.status === "active";

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div
        className="max-w-5xl mx-auto py-4 sm:py-8 px-2"
        data-testid="pricing-page"
      >
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            Choose Your Plan
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base max-w-lg mx-auto">
            Simple, transparent pricing. All plans include a 14-day free trial.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {plans.map((plan) => {
            const isPopular = plan.popular;
            const isCurrent = isActive && currentPlan === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border bg-white p-6 sm:p-8 flex flex-col transition-all ${
                  isPopular
                    ? "border-2 border-indigo-500 shadow-lg shadow-indigo-100 scale-[1.02]"
                    : "border-gray-200 shadow-sm"
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 bg-indigo-500 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                {/* Plan header */}
                <div className="mb-6">
                  <div
                    className={`inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3 ${
                      isPopular
                        ? "bg-indigo-50 text-indigo-600"
                        : "bg-gray-50 text-gray-600"
                    }`}
                  >
                    {planIcons[plan.id] || <Sparkles className="w-6 h-6" />}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-extrabold text-gray-900">
                      ${plan.price}
                    </span>
                    <span className="text-gray-500 text-sm">
                      /{plan.interval}
                    </span>
                  </div>
                  {plan.subtitle && (
                    <p className="text-gray-500 text-sm mt-1.5">
                      {plan.subtitle}
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check
                        className={`w-4 h-4 mt-0.5 shrink-0 ${
                          isPopular ? "text-indigo-500" : "text-emerald-500"
                        }`}
                      />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  className={`w-full h-11 font-semibold rounded-xl transition-all ${
                    isPopular
                      ? "bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm"
                      : "bg-gray-900 hover:bg-gray-800 text-white"
                  }`}
                  disabled={isCurrent || subscribing === plan.id}
                  onClick={() => handleSubscribe(plan.id)}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {subscribing === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to payment...
                    </>
                  ) : isCurrent ? (
                    "Current Plan"
                  ) : isActive ? (
                    "Switch to " + plan.name
                  ) : (
                    "Get Started"
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ / Note */}
        <div className="text-center mt-8 sm:mt-12 text-sm text-gray-400 space-y-1">
          <p>All prices in AUD. Cancel anytime from Settings.</p>
          <p>
            Payments processed securely by Stripe. No card data stored on our
            servers.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PricingPage;
